import React from 'react';
import '../styles/floating-voice-button.css';

/**
 * FloatingVoiceButton - Always-accessible voice recording button
 * Floats above content and provides prominent voice interaction entry point
 * Doubles as "End Call" button when VAPI call is active
 */
const FloatingVoiceButton = ({ 
  onClick, 
  isRecording, 
  context,
  vertical = 'business',
  activeTab = 'chat',
  disabled = false
}) => {
  // Contextual labels based on current tab
  const getContextLabel = () => {
    if (isRecording) return 'Tap to end call';
    if (context) return context;
    
    switch (activeTab) {
      case 'documents':
        return 'Ask about this doc';
      case 'insights':
        return 'Voice search';
      case 'coach':
        return 'Ask about notes';
      default:
        return 'Tap to start voice';
    }
  };

  const contextLabel = getContextLabel();

  return (
    <button 
      className={`ov-floating-voice-btn ${isRecording ? 'recording' : ''} ${disabled ? 'disabled' : ''} ov-fab-${vertical}`}
      onClick={onClick}
      disabled={disabled && !isRecording}
      aria-label={isRecording ? 'End voice call' : (disabled ? 'Voice not available on your plan' : 'Start voice call')}
      title={isRecording ? 'Tap to end call' : (disabled ? 'Voice available on Professional tier and above' : (contextLabel || 'Tap to speak'))}
    >
      {/* Pulsing ring animation */}
      {!isRecording && (
        <>
          <div className="ov-fab-pulse-ring" aria-hidden="true" />
          <div className="ov-fab-pulse-ring-delayed" aria-hidden="true" />
        </>
      )}
      
      {/* Icon - Mic when idle, X when recording */}
      {isRecording ? (
        <svg 
          className="ov-fab-mic-icon" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path 
            d="M18 6L6 18M6 6L18 18" 
            stroke="currentColor" 
            strokeWidth="3" 
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg 
          className="ov-fab-mic-icon" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path 
            d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z" 
            fill="currentColor"
          />
          <path 
            d="M19 10V12C19 15.9 15.9 19 12 19C8.1 19 5 15.9 5 12V10H3V12C3 16.4 6.6 20 11 20V22H13V20C17.4 20 21 16.4 21 12V10H19Z" 
            fill="currentColor"
          />
        </svg>
      )}
      
      {/* Contextual label */}
      <span className="ov-fab-label">{contextLabel}</span>
    </button>
  );
};

export default FloatingVoiceButton;
